import {
    Box,
    HStack,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { getReadableOnsiteAvailability, PropertyKeyAvailableType } from '@acp/shared';
import { format } from 'date-fns';
import { ThemeTokens } from '@acp/core-ui';
import { EditPropertyFormValues } from './form-schema';
import { useProperty } from '../../hooks/use-property';

export function KeyOnsiteConfirmation({
    propertyId,
}: {
    propertyId: number;
}) {
    const {
        getValues,
    } = useFormContext<EditPropertyFormValues>();

    const {
        onsiteAvailability,
        temporaryKeyExpirationDate,
        keyOnsiteNotes,
    } = getValues().propertyKey ?? {
        onsiteAvailability: PropertyKeyAvailableType.Unavailable,
        temporaryKeyExpirationDate: null,
        keyOnsiteNotes: '',
    };

    const propertyQuery = useProperty(propertyId);

    const validFrom = useMemo(() => {
        if (propertyQuery.data === undefined) {
            return null;
        }
        if (propertyQuery.data.propertyKeyInformation.validFrom === null) {
            return format(new Date(), 'yyyy-MM-dd');
        }

        return propertyQuery.data.propertyKeyInformation.validFrom;
    }, [propertyQuery.data]);

    return (
        <Box
            p={6}
            borderColor={ThemeTokens.Defaults.borderColor}
            borderWidth={1}
            borderRadius="md"
        >
            <VStack gap={6}>
                <Box w="full">
                    <Text fontWeight="medium" fontSize="md">
                        Is a key onsite at the property?
                    </Text>
                    <Text>{getReadableOnsiteAvailability(onsiteAvailability)}</Text>
                </Box>

                {onsiteAvailability !== PropertyKeyAvailableType.Unavailable && (
                    <>
                        <Box w="full">
                            <Text fontWeight="medium" fontSize="md">Available</Text>
                            <HStack gap={6}>
                                <Box>
                                    <Text fontSize="sm" color="muted.500">
                                        From
                                    </Text>
                                    {validFrom !== null && (
                                        <Text>{format(validFrom, 'dd/MM/yyyy')}</Text>
                                    )}
                                </Box>

                                {onsiteAvailability === PropertyKeyAvailableType.Temporary
                                && temporaryKeyExpirationDate !== null && (
                                    <Box>
                                        <Text fontSize="sm" color="muted.500">
                                            To
                                        </Text>
                                        <Text fontSize="md">
                                            {format(temporaryKeyExpirationDate, 'dd/MM/yyyy')}
                                        </Text>
                                    </Box>
                                )}
                            </HStack>
                        </Box>
                        <Box w="full">
                            <Text fontWeight="medium" fontSize="md">Key onsite notes</Text>
                            <Text>{keyOnsiteNotes ?? 'None'}</Text>
                        </Box>
                    </>
                )}
            </VStack>
        </Box>
    );
}
